import { useState } from "react";
import { Avatar, type AvatarUser } from "@/components/ui/Avatar";
import { DoneBox } from "@/components/ui/DoneBox";
import { Icon } from "@/components/ui/Icon";
import { RollingNumber } from "@/components/ui/RollingNumber";
import { StatusPill } from "@/components/ui/StatusPill";
import { deriveAvatarColor, deriveInitials } from "@/lib/colors";
import { fmtDateLong } from "@/lib/format";
import { t } from "@/lib/strings";
import {
  isSubtaskDone,
  subtaskCheckboxDisabled,
  workflowStatusColumn,
  type SubtaskBoard,
} from "@/lib/subtasks";
import type { BoardPerson, ColumnDef, Row } from "@/lib/types";

/**
 * Unteraufgaben — the sub-tasks of one row, where the work actually happens.
 *
 * A sub-task is a FULL board row (`rows.parent_row_id`, MAX_ROW_DEPTH = 1) carrying every
 * column the board has. This section is a focused lens on four of them: the title, the
 * workflow status (as a tick), the assignee and the due date. The parent owns the writes —
 * this component only says WHAT changed, never HOW it is persisted.
 *
 * Three rules this component exists to keep, each a bug this repo has already shipped:
 *
 *  1. 🔴 done is by ROLE, never by label — see lib/subtasks.ts. The Protokoll's
 *     "2 von 5 erledigt" pill counts it the same way; a second definition would drift.
 *  2. 🔴 the checkbox may never write a TICKET board's workflow status (action-only:
 *     comment + recipient + notification, and a closed option stamps resolved_at). Where
 *     `subtaskCheckboxDisabled` says so, a read-only StatusPill renders INSTEAD.
 *  3. 🔴 the Fällig input is DRAFT + EXPLICIT CONFIRM, with no onBlur. A native date input
 *     fires `change` per SEGMENT, so committing on change saves the half-typed `0002-09-30`.
 *     That corrupted 10 real deadlines on prod (ee8fcc5). Same pattern as MemoEntryRow's Frist.
 *
 * The assignee picker is INLINE (a plain input + a list rendered in place), never a portaled
 * popover: a portal inside the detail panel/sheet does not receive keystrokes — this repo has
 * shipped that focus-trap bug twice. Same shape as ShareRowDialog's inline person search.
 *
 * 🔴 A control the board cannot honour is NOT rendered: no person column ⇒ no assignee button
 * (picking someone would write nowhere), no workflow status column ⇒ no checkbox (it would sit
 * there forever unchecked, doing nothing on click). A dead control is worse than a missing one.
 */

export interface SubtaskSectionProps {
  board: SubtaskBoard;
  parentRow: Row;
  subtasks: Row[];
  /** The BOARD's people directory: workspace members PLUS cross-department guests. */
  members: BoardPerson[];
  onCreate: (title: string, assigneeId: string | null) => void;
  onToggleDone: (rowId: string, done: boolean) => void;
  onSetAssignee: (rowId: string, userId: string | null) => void;
  onSetDue: (rowId: string, due: string | null) => void;
  onOpen: (rowId: string) => void;
  canWrite: boolean;
  /** Optional: rename a sub-task in place. Omitted ⇒ the title renders as static text. */
  onRename?: (rowId: string, title: string) => void;
}

function toAvatarUser(m: BoardPerson): AvatarUser {
  const name = m.display_name ?? m.email;
  return {
    id: m.user_id,
    display_name: name,
    initials: deriveInitials(name),
    avatar_color: deriveAvatarColor(m.user_id),
    username: m.username,
    department_workspace_id: m.department_workspace_id ?? null,
    department_color: m.department_color ?? null,
  };
}

function memberName(m: BoardPerson): string {
  return m.display_name ?? m.email;
}

function firstColumn(board: SubtaskBoard, type: ColumnDef["type"]): ColumnDef | null {
  return board.columns.find((c) => c.type === type) ?? null;
}

function cellOf(row: Row, col: ColumnDef | null): unknown {
  if (!col) return undefined;
  return row.cells?.find((c) => c.column_def_id === col.id)?.value;
}

function titleOf(row: Row, col: ColumnDef | null): string {
  const v = cellOf(row, col);
  return typeof v === "string" ? v : "";
}

function assigneeIdOf(row: Row, col: ColumnDef | null): string | null {
  const v = cellOf(row, col);
  if (Array.isArray(v) && typeof v[0] === "string") return v[0];
  if (typeof v === "string" && v) return v;
  return null;
}

function dueOf(row: Row, col: ColumnDef | null): string | null {
  const v = cellOf(row, col);
  return typeof v === "string" && v ? v : null;
}

const inputStyle = {
  height: 30,
  padding: "0 9px",
  fontSize: 13,
  borderRadius: 8,
  border: "1px solid var(--hairline-strong)",
  background: "var(--surface)",
  color: "var(--ink-1)",
  outline: "none",
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
} as const;

/** Inline (never portaled) assignee picker: a search input + a filtered list, in place. */
function AssigneePicker({
  members,
  value,
  onPick,
  onClose,
}: {
  members: BoardPerson[];
  value: string | null;
  onPick: (userId: string | null) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const results = needle
    ? members.filter(
        (m) =>
          memberName(m).toLowerCase().includes(needle) ||
          (m.username ?? "").toLowerCase().includes(needle) ||
          m.email.toLowerCase().includes(needle),
      )
    : members;

  return (
    <div
      data-testid="subtask-assignee-picker"
      style={{
        marginTop: 6,
        padding: 6,
        border: "1px solid var(--hairline-soft)",
        borderRadius: 8,
        background: "var(--surface)",
      }}
    >
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
        placeholder={t.assignSearchPlaceholder}
        aria-label={t.assignSearchPlaceholder}
        style={{ ...inputStyle, width: "100%" }}
      />
      <div style={{ maxHeight: 180, overflowY: "auto", marginTop: 4 }}>
        <button
          type="button"
          onClick={() => onPick(null)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 8px",
            background: "transparent",
            border: "none",
            cursor: "pointer",
            textAlign: "left",
            fontSize: 12.5,
            color: value ? "var(--ink-2)" : "var(--ink-4)",
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
          }}
        >
          <Icon name="x" size={12} color="var(--ink-4)" />
          {t.subtaskAssignNobody}
        </button>
        {results.map((m) => (
          <button
            key={m.user_id}
            type="button"
            onClick={() => onPick(m.user_id)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 8px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              textAlign: "left",
              fontSize: 13,
              color: "var(--ink-1)",
              fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            }}
          >
            <Avatar user={toAvatarUser(m)} size={20} />
            <span style={{ fontWeight: value === m.user_id ? 600 : 500 }}>{memberName(m)}</span>
          </button>
        ))}
        {results.length === 0 ? (
          <div style={{ padding: "8px", fontSize: 12.5, color: "var(--ink-4)" }}>
            {t.assignNoResults}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The Fällig control: draft + explicit confirm. NO onBlur — see the section doc-comment. */
function DueControl({
  due,
  canWrite,
  onSetDue,
}: {
  due: string | null;
  canWrite: boolean;
  onSetDue: (due: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  if (!canWrite) {
    // German date, never the raw ISO — "01. Aug. 2026", not "2026-08-01".
    return due ? (
      <span style={{ fontSize: 12, color: "var(--ink-3)", whiteSpace: "nowrap" }}>
        {fmtDateLong(due)}
      </span>
    ) : null;
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(due ?? "");
          setOpen(true);
        }}
        title={t.subtaskDue}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 12,
          fontWeight: 600,
          padding: "3px 8px",
          borderRadius: 8,
          border: "1px solid var(--hairline)",
          background: "var(--surface-soft)",
          color: due ? "var(--ink-1)" : "var(--ink-4)",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        <Icon name="date" size={12} />
        {due ? fmtDateLong(due) : t.subtaskDue}
      </button>
    );
  }

  // 🔴 DRAFT-ONLY. A native date input fires `change` per SEGMENT: typing 30 → 09 → the first
  // digit of the year briefly forms `0002-09-30`, a truthy, "valid" date. Committing on change
  // saved that and slammed the picker shut — it corrupted 10 real deadlines on prod (ee8fcc5).
  // Enter or the ✓ commits, Esc discards. There is deliberately NO onBlur: a blur fires the
  // instant the user reaches for the ✓.
  const commit = () => {
    if (!draft) return;
    onSetDue(draft);
    setOpen(false);
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
      <input
        type="date"
        autoFocus
        aria-label={t.subtaskDue}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") setOpen(false);
        }}
        style={{ ...inputStyle, height: 28, fontSize: 12 }}
      />
      <button
        type="button"
        title={t.subtaskDueConfirm}
        aria-label={t.subtaskDueConfirm}
        disabled={!draft}
        onClick={commit}
        style={{
          background: "transparent",
          border: "none",
          cursor: draft ? "pointer" : "default",
          padding: 2,
          lineHeight: 0,
        }}
      >
        <Icon name="check" size={15} color="var(--ink-2)" />
      </button>
      {due ? (
        <button
          type="button"
          title={t.subtaskDueClear}
          aria-label={t.subtaskDueClear}
          onClick={() => {
            onSetDue(null);
            setOpen(false);
          }}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 2,
            lineHeight: 0,
          }}
        >
          <Icon name="x" size={13} color="var(--ink-4)" />
        </button>
      ) : null}
    </span>
  );
}

function SubtaskLine({
  board,
  row,
  statusCol,
  titleCol,
  personCol,
  dateCol,
  members,
  canWrite,
  onToggleDone,
  onSetAssignee,
  onSetDue,
  onOpen,
  onRename,
}: {
  board: SubtaskBoard;
  row: Row;
  statusCol: ColumnDef | null;
  titleCol: ColumnDef | null;
  personCol: ColumnDef | null;
  dateCol: ColumnDef | null;
  members: BoardPerson[];
  canWrite: boolean;
  onToggleDone: (rowId: string, done: boolean) => void;
  onSetAssignee: (rowId: string, userId: string | null) => void;
  onSetDue: (rowId: string, due: string | null) => void;
  onOpen: (rowId: string) => void;
  onRename?: (rowId: string, title: string) => void;
}) {
  const title = titleOf(row, titleCol);
  const done = isSubtaskDone(row, statusCol);
  const statusReadOnly = subtaskCheckboxDisabled(board, statusCol);
  const assigneeId = assigneeIdOf(row, personCol);
  const assignee = members.find((m) => m.user_id === assigneeId) ?? null;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState<string | null>(null);

  const statusValue = cellOf(row, statusCol);
  const statusOpt =
    statusCol?.config?.options?.find((o) => o.id === statusValue) ??
    (statusCol && typeof statusValue === "string"
      ? { id: statusValue, label: statusValue, color: "" }
      : null);

  const commitTitle = () => {
    // No draft in flight ⇒ nothing to commit. Keeps Enter-then-blur (and Escape-then-blur)
    // from firing the rename twice.
    if (titleDraft === null) return;
    const next = titleDraft.trim();
    setTitleDraft(null);
    if (onRename && next && next !== title) onRename(row.id, next);
  };

  return (
    <div
      data-testid="subtask-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 0",
        borderTop: "1px solid var(--hairline-soft)",
        flexWrap: "wrap",
      }}
    >
      {/* 🔴 No workflow status column ⇒ NO checkbox. There is nowhere for "done" to land: the
          tick would render permanently unchecked and a click would do nothing at all.
          🔴 Ticket boards: the workflow status is action-only. A read-only pill, not a tick. */}
      {!statusCol ? null : statusReadOnly ? (
        <span data-testid="subtask-status-readonly" title={t.subtaskStatusReadOnly}>
          <StatusPill option={statusOpt} size="xs" />
        </span>
      ) : (
        <button
          type="button"
          role="checkbox"
          aria-checked={done}
          data-testid="subtask-checkbox"
          aria-label={title || t.subtaskSectionTitle}
          disabled={!canWrite}
          onClick={() => {
            // Belt AND braces: `disabled` is a DOM hint, this is the rule. A viewer's click
            // must never reach the mutation.
            if (!canWrite) return;
            onToggleDone(row.id, !done);
          }}
          style={{
            padding: 0,
            border: "none",
            background: "transparent",
            display: "flex",
            flexShrink: 0,
            cursor: canWrite ? "pointer" : "default",
          }}
        >
          <DoneBox checked={done} size={16} />
        </button>
      )}

      {/* Title — inline-editable when the parent supplied onRename (blur/Enter is fine here:
          a text input has no per-segment change storm; only the DATE input needs draft+confirm). */}
      {onRename && canWrite ? (
        <input
          value={titleDraft ?? title}
          aria-label={t.subtaskTitleLabel}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitTitle();
            else if (e.key === "Escape") setTitleDraft(null);
          }}
          style={{
            ...inputStyle,
            flex: 1,
            minWidth: 120,
            border: "1px solid transparent",
            background: "transparent",
            textDecoration: done ? "line-through" : "none",
            color: done ? "var(--ink-3)" : "var(--ink-1)",
          }}
        />
      ) : (
        <span
          className="flow-strike"
          data-done={done}
          style={{
            flex: 1,
            minWidth: 120,
            fontSize: 13,
            color: done ? "var(--ink-3)" : "var(--ink-1)",
            transition: "color var(--flow-motion-fast) var(--flow-ease-out)",
          }}
        >
          {title}
        </span>
      )}

      {/* Assignee — avatar + INLINE picker (never a portaled popover). 🔴 No person column ⇒
          no button: picking someone would write nowhere. The same rule DueControl already
          keeps for a missing date column. */}
      {personCol ? (
        <button
          type="button"
          data-testid="subtask-assignee"
          title={assignee ? memberName(assignee) : t.subtaskAssignNobody}
          disabled={!canWrite}
          onClick={() => setPickerOpen((v) => !v)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            fontWeight: 600,
            padding: "3px 8px",
            borderRadius: 8,
            border: "1px solid var(--hairline)",
            background: "var(--surface-soft)",
            color: "var(--ink-1)",
            cursor: canWrite ? "pointer" : "default",
            whiteSpace: "nowrap",
          }}
        >
          {assignee ? (
            <Avatar user={toAvatarUser(assignee)} size={18} />
          ) : (
            <Icon name="person" size={13} color="var(--accent)" />
          )}
          {assignee ? memberName(assignee) : t.subtaskAssignNobody}
        </button>
      ) : null}

      <DueControl
        due={dueOf(row, dateCol)}
        canWrite={canWrite && !!dateCol}
        onSetDue={(v) => onSetDue(row.id, v)}
      />

      <button
        type="button"
        onClick={() => onOpen(row.id)}
        style={{
          background: "none",
          border: "none",
          padding: "3px 4px",
          fontSize: 12,
          fontWeight: 600,
          color: "var(--accent)",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {t.subtaskOpen}
      </button>

      {pickerOpen && canWrite ? (
        <div style={{ flexBasis: "100%" }}>
          <AssigneePicker
            members={members}
            value={assigneeId}
            onPick={(userId) => {
              onSetAssignee(row.id, userId);
              setPickerOpen(false);
            }}
            onClose={() => setPickerOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}

export function SubtaskSection({
  board,
  parentRow,
  subtasks,
  members,
  onCreate,
  onToggleDone,
  onSetAssignee,
  onSetDue,
  onOpen,
  canWrite,
  onRename,
}: SubtaskSectionProps) {
  const [title, setTitle] = useState("");
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const statusCol = workflowStatusColumn(board);
  const titleCol = firstColumn(board, "text");
  const personCol = firstColumn(board, "person");
  const dateCol = firstColumn(board, "date");

  // Nothing to show and nothing to add ⇒ no empty chrome in a read-only panel.
  if (subtasks.length === 0 && !canWrite) return null;

  const create = () => {
    const next = title.trim();
    if (!next) return;
    // ONE call: the title AND the assignee land together, so a sub-task is never briefly
    // unassigned (and never fires a second "assigned to you" notification a beat later).
    onCreate(next, assigneeId);
    setTitle("");
    setAssigneeId(null);
    setPickerOpen(false);
  };

  const pending = assigneeId ? (members.find((m) => m.user_id === assigneeId) ?? null) : null;

  return (
    <section data-testid="subtask-section" data-parent-row={parentRow.id} style={{ marginTop: 18 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: "uppercase",
          color: "var(--ink-4)",
          marginBottom: 6,
        }}
      >
        {t.subtaskSectionTitle}
        {subtasks.length > 0 ? (
          <span style={{ marginLeft: 6, fontWeight: 600 }}>
            <RollingNumber value={subtasks.filter((r) => isSubtaskDone(r, statusCol)).length} />/
            {subtasks.length}
          </span>
        ) : null}
      </div>

      {subtasks.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "var(--ink-4)", padding: "4px 0" }}>
          {t.subtaskEmpty}
        </div>
      ) : (
        subtasks.map((row) => (
          <SubtaskLine
            key={row.id}
            board={board}
            row={row}
            statusCol={statusCol}
            titleCol={titleCol}
            personCol={personCol}
            dateCol={dateCol}
            members={members}
            canWrite={canWrite}
            onToggleDone={onToggleDone}
            onSetAssignee={onSetAssignee}
            onSetDue={onSetDue}
            onOpen={onOpen}
            {...(onRename ? { onRename } : {})}
          />
        ))
      )}

      {canWrite ? (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <input
              value={title}
              aria-label={t.subtaskNew}
              placeholder={t.subtaskNew}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") create();
              }}
              style={{ ...inputStyle, flex: 1, minWidth: 140 }}
            />
            {/* 🔴 Same rule as the line's assignee: no person column ⇒ no picker. The create
                path would silently drop the assignee — there is no cell to write it to. */}
            {personCol ? (
              <button
                type="button"
                data-testid="subtask-new-assignee"
                title={pending ? memberName(pending) : t.subtaskAssignNobody}
                onClick={() => setPickerOpen((v) => !v)}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "5px 9px",
                  borderRadius: 8,
                  border: "1px solid var(--hairline)",
                  background: "var(--surface-soft)",
                  color: "var(--ink-1)",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {pending ? (
                  <Avatar user={toAvatarUser(pending)} size={18} />
                ) : (
                  <Icon name="person" size={13} color="var(--accent)" />
                )}
                {pending ? memberName(pending) : t.subtaskAssignNobody}
              </button>
            ) : null}
            <button
              type="button"
              onClick={create}
              disabled={!title.trim()}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontSize: 12.5,
                fontWeight: 600,
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid var(--hairline)",
                background: "var(--surface-soft)",
                color: title.trim() ? "var(--accent)" : "var(--ink-4)",
                cursor: title.trim() ? "pointer" : "default",
                whiteSpace: "nowrap",
              }}
            >
              <Icon name="plus" size={12} />
              {t.subtaskAddShort}
            </button>
          </div>
          {pickerOpen ? (
            <AssigneePicker
              members={members}
              value={assigneeId}
              onPick={(userId) => {
                setAssigneeId(userId);
                setPickerOpen(false);
              }}
              onClose={() => setPickerOpen(false)}
            />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
